import { Component } from '@theme/component';

/**
 * @typedef {Object} PasswordValidationCopy
 * @property {string} missing
 * @property {string} length
 * @property {string} lower
 * @property {string} upper
 * @property {string} number
 * @property {string} special
 * @property {string} invalid
 */

/**
 * @extends {Component}
 */
class CustomerFormValidation extends Component {
  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {number} */
  maxLength = 250;

  /** @type {string} */
  requiredMessage = '';

  /** @type {string} */
  maxLengthMessage = '';

  /** @type {string} */
  emailInvalidMessage = '';

  /** @type {boolean} */
  enablePasswordStrength = false;

  /** @type {boolean} */
  enablePasswordMatch = false;

  /** @type {string} */
  passwordMismatchMessage = '';

  /** @type {HTMLInputElement | null} */
  passwordInput = null;

  /** @type {HTMLInputElement | null} */
  passwordConfirmInput = null;

  /** @type {HTMLElement | null} */
  passwordErrorEl = null;

  /** @type {PasswordValidationCopy | null} */
  passwordCopy = null;

  /** @type {boolean} */
  submitViaFetch = false;

  /** @type {string} */
  successRedirectUrl = '';

  /** @type {number} */
  successRedirectDelayMs = 0;

  /** @type {string} */
  successNoticeSelector = '';

  /** @type {boolean} */
  #bypassNextSubmitHandler = false;

  /** @type {'register' | 'login' | ''} */
  reactivationMode = '';

  /** @type {string} */
  reactivationAppProxyPath = '/apps/gastro-hero-cust-reactivation';

  /** @type {string} */
  activationRedirectUrl = '';

  /** @type {boolean} */
  enableEmailNotFoundCheck = false;

  /** @type {boolean} */
  #reactivationCheckInProgress = false;

  /**
   * @type {boolean}
   */
  #suppressNativeLoginSubmit = false;

  static REASON = {
    NOT_FOUND: 'CUSTOMER_NOT_FOUND',
    ALREADY_ACTIVE: 'CUSTOMER_ALREADY_ACTIVE',
    NOT_ACTIVATED: 'IMPORTED_CUSTOMER_NOT_ACTIVATED',
  };

  static REGISTER_UNACTIVATED_REDIRECT_KEY = 'gh-register-unactivated-redirect';

  static REACTIVATION_ENDPOINT_SUFFIX = '/customer-reactivation';

  /**
   * @param {string | null | undefined} proxyPath
   * @returns {string}
   */
  static buildReactivationEndpoint(proxyPath) {
    const fallback = '/apps/gastro-hero-cust-reactivation';
    let raw =
      String(proxyPath ?? '')
        .trim()
        .replace(/\\/g, '') || fallback;

    if (/^https?:\/\//i.test(raw)) {
      try {
        const parsed = new URL(raw);
        if (parsed.hostname === 'apps') {
          const rest = parsed.pathname.replace(/^\/+/, '');
          raw = rest ? `/apps/${rest}` : fallback;
        } else {
          raw = parsed.pathname || fallback;
        }
      } catch {
        raw = fallback;
      }
    }

    raw = raw.replace(/^\/+/, '/');
    if (!raw.startsWith('/')) {
      raw = `/${raw}`;
    }
    raw = raw.replace(/\/{2,}/g, '/').replace(/\/+$/, '') || fallback;

    if (!raw.startsWith('/apps/')) {
      raw = fallback;
    }

    const suffix = CustomerFormValidation.REACTIVATION_ENDPOINT_SUFFIX;
    const path = raw.endsWith(suffix) ? raw : `${raw}${suffix}`;

    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${path}`;
    }

    return path;
  }

  /**
   * @returns {string}
   */
  #getReactivationEndpoint() {
    return CustomerFormValidation.buildReactivationEndpoint(
      this.getAttribute('data-reactivation-app-proxy-path') || this.reactivationAppProxyPath
    );
  }

  connectedCallback() {
    super.connectedCallback();

    this.form = this.closest('form');

    if (!this.form) return;

    this.maxLength = this.#readNumberAttr('data-max-length', 250);
    this.requiredMessage = this.getAttribute('data-required-message') || '';
    this.maxLengthMessage = this.getAttribute('data-max-length-message') || '';
    this.emailInvalidMessage = this.getAttribute('data-email-invalid-message') || '';
    this.enablePasswordStrength = this.getAttribute('data-enable-password-strength') === 'true';
    this.enablePasswordMatch = this.getAttribute('data-enable-password-match') === 'true';
    this.passwordMismatchMessage = this.getAttribute('data-password-mismatch-message') || '';
    this.submitViaFetch = this.getAttribute('data-submit-via-fetch') === 'true';
    this.successRedirectUrl = this.getAttribute('data-success-redirect-url') || '';
    this.successRedirectDelayMs = this.#readNumberAttr('data-success-redirect-delay', 0);
    this.successNoticeSelector = this.getAttribute('data-success-notice-selector') || '';
    const reactivationModeAttr = this.getAttribute('data-reactivation-mode');
    this.reactivationMode =
      reactivationModeAttr === 'register' || reactivationModeAttr === 'login'
        ? reactivationModeAttr
        : '';
    this.reactivationAppProxyPath =
      this.getAttribute('data-reactivation-app-proxy-path') || this.reactivationAppProxyPath;
    this.activationRedirectUrl = this.getAttribute('data-activation-redirect-url') || '';
    this.enableEmailNotFoundCheck =
      this.getAttribute('data-enable-email-not-found-check') === 'true';

    this.passwordInput = /** @type {HTMLInputElement | null} */ (
      this.refs.passwordInput ??
        this.form.querySelector('input[type="password"][name="customer[password]"]')
    );
    this.passwordConfirmInput = /** @type {HTMLInputElement | null} */ (
      this.refs.passwordConfirmInput ??
        this.form.querySelector('input[type="password"][name="customer[password_confirmation]"]')
    );

    this.#bindTextAndEmailValidation();
    this.#bindBasicPasswordValidation();
    this.#bindPasswordStrengthValidation();
    this.#bindPasswordMatchValidation();
    this.#bindFormSubmitGuard();
    this.#bindReactivationSubmit();
    this.#bindSubmitRedirect();
    this.#showAccountExistsFromReturnUrl();
  }

  #bindBasicPasswordValidation() {
    if (!this.form) return;

    const passwordFields = Array.from(this.form.querySelectorAll('input[type="password"]'));

    passwordFields.forEach((field) => {
      if (!(field instanceof HTMLInputElement)) return;
      if (this.enablePasswordStrength && field === this.passwordInput) return;
      if (this.enablePasswordMatch && field === this.passwordConfirmInput) return;

      field.addEventListener('input', () => {
        field.setCustomValidity('');
        this.#restoreDefaultFieldErrorMessage(field);
        this.#clearValidationClasses(field);
      });

      field.addEventListener('blur', () => {
        this.#validateBasicPasswordField(field);
      });
    });
  }

  /**
   * @param {HTMLInputElement} field
   */
  #validateBasicPasswordField(field) {
    const trimmed = (field.value ?? '').trim();

    if (!field.required && trimmed.length === 0) {
      field.setCustomValidity('');
      this.#restoreDefaultFieldErrorMessage(field);
      this.#clearValidationClasses(field);
      return;
    }

    if (field.required && trimmed.length === 0) {
      this.#setFieldErrorMessage(field, this.requiredMessage || 'Required');
      field.setCustomValidity(this.requiredMessage || 'Required');
      this.#markInvalid(field);
      return;
    }

    field.setCustomValidity('');
    this.#restoreDefaultFieldErrorMessage(field);
    this.#markValid(field);
  }

  #bindReactivationSubmit() {
    const form = this.form;
    if (!form || !this.reactivationMode) return;

    form.addEventListener('submit', async (e) => {
      if (this.#suppressNativeLoginSubmit) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (this.#bypassNextSubmitHandler) {
        this.#bypassNextSubmitHandler = false;
        return;
      }

      if (this.#reactivationCheckInProgress) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!form.checkValidity()) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const email = this.#getFormEmail();
      if (!email) return;

      this.#hideReactivationNotices();
      this.#setSubmitBusy(true);
      this.#reactivationCheckInProgress = true;

      try {
        if (this.reactivationMode === 'register') {
          await this.#handleRegisterReactivation(email);
        } else if (this.reactivationMode === 'login') {
          await this.#handleLoginReactivation(email);
        }
      } catch {
        if (!this.#suppressNativeLoginSubmit) {
          this.#nativeSubmit();
        }
      } finally {
        this.#reactivationCheckInProgress = false;
        if (!this.#suppressNativeLoginSubmit) {
          this.#setSubmitBusy(false);
        }
      }
    });
  }

  /**
   * @param {string} email
   */
  async #handleRegisterReactivation(email) {
    const result = await this.#checkCustomerReactivation(email, false);

    if (!result?.success || !result.data) {
      this.#nativeSubmit();
      return;
    }

    const reason = result.data.reason;

    if (reason === CustomerFormValidation.REASON.NOT_ACTIVATED) {
      const redirectUrl = this.#getActivationRedirectUrl();
      if (redirectUrl) {
        this.#storeUnactivatedRegisterRedirect(redirectUrl);
      }
      this.#nativeSubmit();
      return;
    }

    if (reason === CustomerFormValidation.REASON.NOT_FOUND) {
      if (this.enableEmailNotFoundCheck) {
        this.#showReactivationNotice('.js-reactivation-email-not-found');
        return;
      }
      this.#nativeSubmit();
      return;
    }

    if (reason === CustomerFormValidation.REASON.ALREADY_ACTIVE) {
      this.#showReactivationNotice('.js-reactivation-already-active');
      return;
    }

    this.#nativeSubmit();
  }

  /**
   * @param {string} email
   */
  async #handleLoginReactivation(email) {
    const statusResult = await this.#checkCustomerReactivation(email, false);
    const reason = statusResult?.data?.reason;

    if (reason === CustomerFormValidation.REASON.NOT_ACTIVATED) {
      const redirectUrl = this.#getActivationRedirectUrl();

      try {
        await this.#checkCustomerReactivation(email, true);
      } catch {
        // Invite may still have been sent; continue to redirect or native submit.
      }

      if (redirectUrl) {
        // Only lock the form once we know we can leave the page. Persist first so a
        // CAPTCHA / native login re-submit that wins the navigation race still lands
        // on the info page via customer-reactivation-redirect.js.
        this.#suppressNativeLoginSubmit = true;
        this.#preventFurtherFormSubmits();
        this.#storeUnactivatedRegisterRedirect(redirectUrl);
        window.location.replace(redirectUrl);
        return;
      }

      // No activation info page configured — do not permanently lock the form.
      this.#nativeSubmit();
      return;
    }

    this.#nativeSubmit();
  }

  /**
   * Blocks native / programmatic form submits after we take over the login flow.
   * Shopify CAPTCHA may call form.submit() which does not fire submit events.
   */
  #preventFurtherFormSubmits() {
    const form = this.form;
    if (!form) return;

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    form.submit = () => {};
  }

  /**
   * @param {string} redirectUrl
   */
  #storeUnactivatedRegisterRedirect(redirectUrl) {
    try {
      window.sessionStorage?.setItem(
        CustomerFormValidation.REGISTER_UNACTIVATED_REDIRECT_KEY,
        JSON.stringify({ url: redirectUrl, at: Date.now() })
      );
    } catch {
      // sessionStorage may be unavailable; create_customer still proceeds.
    }
  }

  /**
   * @returns {string}
   */
  #getActivationRedirectUrl() {
    const fromAttr = this.getAttribute('data-activation-redirect-url');
    let value = (fromAttr || this.activationRedirectUrl || '').trim();
    if (!value) return '';
    value = value.replace(/\\/g, '');

    const pageMatch = value.match(/^shopify:\/\/pages\/([^/?#]+)/i);
    if (pageMatch) {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      return `${root}/pages/${pageMatch[1]}`;
    }

    if (value.startsWith('//')) {
      value = value.replace(/^\/+/, '/');
    }

    if (value.startsWith('/')) {
      return value.replace(/\/{2,}/g, '/');
    }

    if (/^https?:\/\//i.test(value)) {
      try {
        const parsed = new URL(value);
        if (parsed.origin === window.location.origin) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
        }
        return value;
      } catch {
        return '';
      }
    }

    return `/${value}`.replace(/\/{2,}/g, '/');
  }

  /**
   * @param {string} email
   * @param {boolean} sendActivationMail
   * @returns {Promise<{
   *   success: boolean,
   *   data?: {
   *     migrated: boolean,
   *     activationEmailSent: boolean,
   *     reason: string,
   *   },
   *   error?: string,
   *   status?: string
   * } | null>}
   */
  async #checkCustomerReactivation(email, sendActivationMail) {
    try {
      const response = await fetch(this.#getReactivationEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          sendActivationMail: Boolean(sendActivationMail),
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!payload || typeof payload !== 'object') return null;
      return /** @type {{ success: boolean, data?: { migrated: boolean, activationEmailSent: boolean, reason: string }, error?: string, status?: string }} */ (
        payload
      );
    } catch {
      return null;
    }
  }

  /**
   * @returns {string}
   */
  #getFormEmail() {
    const emailInput = this.form?.querySelector(
      'input[type="email"], input[name="customer[email]"], input[name="email"]'
    );
    if (!(emailInput instanceof HTMLInputElement)) return '';
    return (emailInput.value ?? '').trim().toLowerCase();
  }

  /**
   * @param {string} selector
   */
  #showReactivationNotice(selector) {
    const scope =
      this.closest('.customer__form-content') ||
      this.closest('.customer__form-wrapper') ||
      this.form?.parentElement ||
      document;
    const notice = scope.querySelector(selector);
    if (!(notice instanceof HTMLElement)) return;

    notice.removeAttribute('hidden');
    const message = notice.querySelector('.form__message');
    if (message instanceof HTMLElement) {
      message.focus();
    } else {
      notice.focus();
    }
  }

  #hideReactivationNotices() {
    const scope =
      this.closest('.customer__form-content') ||
      this.closest('.customer__form-wrapper') ||
      this.form?.parentElement ||
      document;
    scope
      .querySelectorAll('.js-reactivation-email-not-found, .js-reactivation-already-active')
      .forEach((el) => {
        if (el instanceof HTMLElement) el.setAttribute('hidden', '');
      });
  }

  /**
   * @param {boolean} busy
   */
  #setSubmitBusy(busy) {
    const form = this.form;
    if (!form) return;

    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"], button.customer__submit-button'
    );
    if (!(submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement))
      return;

    if (busy) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.dataset.originalPointerEvents =
        submitButton.style.pointerEvents || submitButton.dataset.originalPointerEvents || '';
      submitButton.style.pointerEvents = 'none';
      return;
    }

    submitButton.removeAttribute('aria-disabled');
    submitButton.removeAttribute('aria-busy');
    submitButton.style.pointerEvents = submitButton.dataset.originalPointerEvents || '';
    delete submitButton.dataset.originalPointerEvents;
  }

  #nativeSubmit() {
    const form = this.form;
    if (!form) return;
    this.#bypassNextSubmitHandler = true;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    HTMLFormElement.prototype.submit.call(form);
  }

  #bindSubmitRedirect() {
    const form = this.form;
    if (!form) return;
    if (!this.submitViaFetch) return;
    if (!this.successRedirectUrl) return;

    form.addEventListener('submit', async (e) => {
      if (this.#bypassNextSubmitHandler) {
        this.#bypassNextSubmitHandler = false;
        return;
      }

      if (!form.checkValidity()) return;

      e.preventDefault();
      e.stopPropagation();

      const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.dataset.originalPointerEvents =
          submitButton.style.pointerEvents || submitButton.dataset.originalPointerEvents || '';
        submitButton.style.pointerEvents = 'none';
      }

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) {
          throw new Error('Submit failed');
        }

        if (this.successNoticeSelector) {
          const scope =
            this.closest('.customer__form-content') ||
            this.closest('.customer__form-wrapper') ||
            form.parentElement ||
            document;
          const notice = scope.querySelector(this.successNoticeSelector);
          if (notice instanceof HTMLElement) {
            notice.removeAttribute('hidden');
            if (notice.getAttribute('tabindex') === '-1') {
              notice.focus();
            }
          }
        }

        window.setTimeout(() => {
          window.location.assign(this.successRedirectUrl);
        }, this.successRedirectDelayMs);
      } catch {
        this.#bypassNextSubmitHandler = true;
        form.submit();
      } finally {
        if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('aria-busy');
          submitButton.style.pointerEvents = submitButton.dataset.originalPointerEvents || '';
          delete submitButton.dataset.originalPointerEvents;
        }
      }
    });
  }

  #getAccountExistsNotice() {
    if (this.getAttribute('data-show-return-url-account-exists') !== 'true') return null;
    return this.form?.querySelector('.js-email-exists-login-notice') ?? null;
  }

  #hideAccountExistsNotice() {
    const notice = this.#getAccountExistsNotice();
    if (notice instanceof HTMLElement) {
      notice.setAttribute('hidden', '');
    }
  }

  #showAccountExistsFromReturnUrl() {
    if (this.getAttribute('data-show-return-url-account-exists') !== 'true') return;

    const storageKey = 'gh-login-notice-account-exists-consumed';
    const lastSubmitKey = 'gh-login-last-submit-at';
    const lastSubmitLifetimeMs = 10_000;
    const searchParams = new URLSearchParams(window.location.search);
    const notice = this.#getAccountExistsNotice();
    if (!(notice instanceof HTMLElement)) return;

    notice.setAttribute('hidden', '');
    if (searchParams.has('return_url')) {
      const lastSubmitAt = Number(window.sessionStorage?.getItem(lastSubmitKey) || 0);
      if (Number.isFinite(lastSubmitAt) && Date.now() - lastSubmitAt < lastSubmitLifetimeMs) {
        return;
      }

      notice.removeAttribute('hidden');
      const message = notice.querySelector('.form__message');
      if (message instanceof HTMLElement) {
        message.focus();
      }

      const baseURL = window.location.origin + window.location.pathname;
      window.history.replaceState(null, '', baseURL);
      window.sessionStorage?.setItem(storageKey, '1');
      return;
    }

    if (window.sessionStorage?.getItem(storageKey) === '1') {
      this.#hideAccountExistsNotice();
    }
  }

  #bindTextAndEmailValidation() {
    if (!this.form) return;

    const fields = Array.from(this.form.querySelectorAll('input, textarea, select'));

    fields.forEach((field) => {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLTextAreaElement ||
          field instanceof HTMLSelectElement
        )
      )
        return;
      if (field instanceof HTMLInputElement && field.type === 'password') return;

      field.addEventListener('input', () => {
        field.setCustomValidity('');
        this.#restoreDefaultFieldErrorMessage(field);
        this.#clearValidationClasses(field);
        if (this.reactivationMode && this.#isEmailField(field)) {
          this.#hideReactivationNotices();
        }
      });

      field.addEventListener('blur', () => {
        this.#validateFieldOnBlur(field);
      });
    });
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   */
  #validateFieldOnBlur(field) {
    const value = field.value ?? '';
    const trimmed = value.trim();

    if (!field.required && trimmed.length === 0) {
      field.setCustomValidity('');
      this.#restoreDefaultFieldErrorMessage(field);
      this.#clearValidationClasses(field);
      return;
    }

    if (field.required && trimmed.length === 0) {
      this.#setFieldErrorMessage(field, this.requiredMessage || 'Required');
      field.setCustomValidity(this.requiredMessage || 'Required');
      this.#markInvalid(field);
      return;
    }

    if (trimmed.length > this.maxLength) {
      this.#setFieldErrorMessage(field, this.maxLengthMessage || 'Too long');
      field.setCustomValidity(this.maxLengthMessage || 'Too long');
      this.#markInvalid(field);
      return;
    }

    if (this.#isEmailField(field)) {
      if (trimmed.length > 0 && !this.#isValidEmail(trimmed)) {
        this.#setFieldErrorMessage(field, this.emailInvalidMessage || 'Invalid email');
        field.setCustomValidity(this.emailInvalidMessage || 'Invalid email');
        this.#markInvalid(field);
        return;
      }
    }

    field.setCustomValidity('');
    this.#restoreDefaultFieldErrorMessage(field);
    this.#markValid(field);
  }

  #bindPasswordStrengthValidation() {
    if (!this.form || !this.enablePasswordStrength || !this.passwordInput) return;

    this.passwordCopy = this.#readPasswordCopy();
    this.passwordErrorEl = this.#ensurePasswordErrorElement(this.passwordInput);

    this.passwordInput.addEventListener('input', () => {
      const input = this.passwordInput;
      if (!input) return;
      this.#validatePassword(input);
    });

    this.passwordInput.addEventListener('blur', () => {
      const input = this.passwordInput;
      if (!input) return;
      this.#validatePassword(input);
    });
  }

  #bindPasswordMatchValidation() {
    if (!this.enablePasswordMatch || !this.passwordConfirmInput || !this.passwordInput) return;

    const sync = () => {
      this.#validatePasswordMatch();
    };

    this.passwordConfirmInput.addEventListener('input', sync);
    this.passwordConfirmInput.addEventListener('blur', sync);

    this.passwordInput.addEventListener('input', () => {
      if (this.passwordConfirmInput?.value) sync();
    });
  }

  #bindFormSubmitGuard() {
    if (!this.form) return;

    this.form.addEventListener(
      'submit',
      (e) => {
        if (this.getAttribute('data-show-return-url-account-exists') === 'true') {
          window.sessionStorage?.setItem('gh-login-last-submit-at', String(Date.now()));
        }

        const fields = Array.from(this.form?.querySelectorAll('input, textarea, select') ?? []);
        fields.forEach((field) => {
          if (
            !(
              field instanceof HTMLInputElement ||
              field instanceof HTMLTextAreaElement ||
              field instanceof HTMLSelectElement
            )
          )
            return;
          if (field instanceof HTMLInputElement && field.type === 'password') {
            if (this.enablePasswordStrength && field === this.passwordInput) return;
            if (this.enablePasswordMatch && field === this.passwordConfirmInput) return;
            this.#validateBasicPasswordField(field);
            return;
          }
          this.#validateFieldOnBlur(field);
        });

        if (this.passwordInput && this.enablePasswordStrength) {
          this.#validatePassword(this.passwordInput);
        }

        if (this.passwordInput && this.enablePasswordMatch && this.passwordConfirmInput) {
          if (this.passwordInput.validity.valid) {
            this.#validatePasswordMatch();
          } else {
            this.passwordConfirmInput.setCustomValidity('');
            this.#restoreDefaultFieldErrorMessage(this.passwordConfirmInput);
            this.#clearValidationClasses(this.passwordConfirmInput);
          }
        }

        const isValid = this.form?.checkValidity() ?? true;

        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          const firstInvalid = this.form?.querySelector(':invalid');
          if (firstInvalid instanceof HTMLElement) firstInvalid.focus();
        } else if (this.getAttribute('data-show-return-url-account-exists') === 'true') {
          window.sessionStorage?.setItem('gh-account-login-pending', '1');
        }
      },
      { capture: true }
    );
  }

  /**
   * @param {HTMLInputElement} input
   */
  #validatePassword(input) {
    const copy = this.passwordCopy;
    const errorEl = this.passwordErrorEl;
    if (!copy || !errorEl) return;

    const value = (input.value ?? '').trim();

    if (value.length === 0) {
      if (input.required) {
        const msg = this.requiredMessage || copy.invalid || 'Required';
        errorEl.textContent = msg;
        errorEl.classList.add('is-visible');
        input.setCustomValidity(msg);
        this.#markInvalid(input);
      } else {
        errorEl.classList.remove('is-visible');
        errorEl.textContent = '';
        input.setCustomValidity('');
        this.#clearValidationClasses(input);
      }
      if (this.enablePasswordMatch && this.passwordConfirmInput?.value) {
        this.#validatePasswordMatch();
      }
      return;
    }

    /** @type {{ ok: boolean, message: string }[]} */
    const checks = [
      { ok: value.length >= 8, message: copy.length },
      { ok: /[a-z]/.test(value), message: copy.lower },
      { ok: /[A-Z]/.test(value), message: copy.upper },
      { ok: /\d/.test(value), message: copy.number },
      { ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value), message: copy.special },
    ];

    const failed = checks.filter((c) => !c.ok).map((c) => c.message);

    if (failed.length > 0) {
      errorEl.replaceChildren();
      errorEl.append(document.createTextNode(`${copy.missing}:`), document.createElement('br'));
      errorEl.append(document.createTextNode(failed.join(', ')));
      errorEl.classList.add('is-visible');
      input.setCustomValidity(copy.invalid);
      this.#markInvalid(input);
    } else {
      errorEl.classList.remove('is-visible');
      errorEl.textContent = '';
      input.setCustomValidity('');
      this.#markValid(input);
    }

    if (this.enablePasswordMatch && this.passwordConfirmInput?.value) {
      this.#validatePasswordMatch();
    }
  }

  /**
   * @returns {boolean}
   */
  #validatePasswordMatch() {
    const password = this.passwordInput;
    const confirm = this.passwordConfirmInput;
    if (!this.enablePasswordMatch || !password || !confirm) return true;

    if (!password.validity.valid) {
      confirm.setCustomValidity('');
      this.#restoreDefaultFieldErrorMessage(confirm);
      this.#clearValidationClasses(confirm);
      return true;
    }

    const confirmVal = (confirm.value ?? '').trim();

    if (confirm.required && confirmVal.length === 0) {
      this.#setFieldErrorMessage(confirm, this.requiredMessage || 'Required');
      confirm.setCustomValidity(this.requiredMessage || 'Required');
      this.#markInvalid(confirm);
      return false;
    }

    const passwordVal = (password.value ?? '').trim();

    if (confirmVal.length > 0 && passwordVal !== confirmVal) {
      const msg = this.passwordMismatchMessage || 'Passwords do not match';
      this.#setFieldErrorMessage(confirm, msg);
      confirm.setCustomValidity(msg);
      this.#markInvalid(confirm);
      return false;
    }

    confirm.setCustomValidity('');
    this.#restoreDefaultFieldErrorMessage(confirm);
    this.#markValid(confirm);
    return true;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @returns {boolean}
   */
  #isEmailField(field) {
    if (!(field instanceof HTMLInputElement)) return false;
    return field.type === 'email' || field.name?.toLowerCase().includes('email') === true;
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    const value = email.trim();
    if (value.length === 0) return false;
    if (/\s/.test(value)) return false;

    const atIndex = value.indexOf('@');
    if (atIndex <= 0) return false;
    if (value.indexOf('@', atIndex + 1) !== -1) return false;

    const local = value.slice(0, atIndex);
    const domain = value.slice(atIndex + 1);
    if (local.length === 0 || domain.length === 0) return false;
    if (local.length > 64) return false;
    if (domain.length > 255) return false;

    if (domain.startsWith('.') || domain.endsWith('.')) return false;
    if (!domain.includes('.')) return false;
    if (domain.includes('..')) return false;

    const labels = domain.split('.');
    const tld = labels[labels.length - 1] || '';
    if (tld.length < 2) return false;
    if (!/^[A-Za-z]{2,63}$/.test(tld)) return false;

    for (const label of labels) {
      if (label.length === 0) return false;
      if (label.length > 63) return false;
      if (label.startsWith('-') || label.endsWith('-')) return false;
      if (!/^[A-Za-z0-9-]+$/.test(label)) return false;
    }

    if (local.startsWith('.') || local.endsWith('.')) return false;
    if (local.includes('..')) return false;
    if (!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) return false;

    return true;
  }

  /**
   * @param {string} attr
   * @param {number} fallback
   * @returns {number}
   */
  #readNumberAttr(attr, fallback) {
    const raw = this.getAttribute(attr);
    if (!raw) return fallback;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return fallback;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @param {string} message
   */
  #setFieldErrorMessage(field, message) {
    const wrapper = field.closest('.customer-field');
    if (!wrapper) return;
    const inlineMismatch = wrapper.querySelector('.customer-password-mismatch');
    if (inlineMismatch instanceof HTMLElement) {
      inlineMismatch.textContent = message;
      inlineMismatch.classList.toggle('is-visible', Boolean(message));
      return;
    }
    const error = wrapper.querySelector('.customer-field__error');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   */
  #restoreDefaultFieldErrorMessage(field) {
    const wrapper = field.closest('.customer-field');
    if (!wrapper) return;
    const inlineMismatch = wrapper.querySelector('.customer-password-mismatch');
    if (inlineMismatch instanceof HTMLElement) {
      inlineMismatch.textContent = '';
      inlineMismatch.classList.remove('is-visible');
      return;
    }
    const error = wrapper.querySelector('.customer-field__error');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   */
  #markInvalid(field) {
    const wrapper = field.closest('.customer-field');
    if (wrapper instanceof HTMLElement) {
      wrapper.classList.add('is-invalid');
      wrapper.classList.remove('is-valid');
    }
    if (field instanceof HTMLElement) {
      field.classList.add('is-invalid');
      field.classList.remove('is-valid');
    }
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   */
  #markValid(field) {
    const wrapper = field.closest('.customer-field');
    if (wrapper instanceof HTMLElement) {
      wrapper.classList.add('is-valid');
      wrapper.classList.remove('is-invalid');
    }
    if (field instanceof HTMLElement) {
      field.classList.add('is-valid');
      field.classList.remove('is-invalid');
    }
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   */
  #clearValidationClasses(field) {
    const wrapper = field.closest('.customer-field');
    if (wrapper instanceof HTMLElement) {
      wrapper.classList.remove('is-valid', 'is-invalid');
    }
    if (field instanceof HTMLElement) {
      field.classList.remove('is-valid', 'is-invalid');
    }
  }

  /**
   * @returns {PasswordValidationCopy}
   */
  #readPasswordCopy() {
    return {
      missing: this.getAttribute('data-password-missing') || 'Password is missing',
      length: this.getAttribute('data-password-length') || 'Password length of 8',
      lower: this.getAttribute('data-password-lower') || 'one lowercase letter',
      upper: this.getAttribute('data-password-upper') || 'one uppercase letter',
      number: this.getAttribute('data-password-number') || 'one number',
      special: this.getAttribute('data-password-special') || 'one special character',
      invalid: this.getAttribute('data-password-invalid') || 'Password does not meet requirements.',
    };
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {HTMLElement}
   */
  #ensurePasswordErrorElement(input) {
    const field = input.closest('.customer-field');
    if (field) {
      const existing = field.querySelector('.customer-password-validation');
      if (existing instanceof HTMLElement) return existing;

      const el = document.createElement('div');
      el.className = 'customer-password-validation';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');

      const toggle = field.querySelector('password-toggle');
      if (toggle && toggle.parentElement) {
        toggle.parentElement.insertBefore(el, toggle);
      } else {
        field.insertBefore(el, input);
      }
      return el;
    }

    const el = document.createElement('div');
    el.className = 'customer-password-validation';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    input.parentElement?.insertBefore(el, input);
    return el;
  }
}

if (!customElements.get('customer-form-validation')) {
  customElements.define('customer-form-validation', CustomerFormValidation);
}
